import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiFetch from '../utils/apiFetch';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import PaletteIcon from '@mui/icons-material/Palette';
import ApiIcon from '@mui/icons-material/Api';
import SchoolIcon from '@mui/icons-material/School';
import BarChartIcon from '@mui/icons-material/BarChart';
import SecurityIcon from '@mui/icons-material/Security';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ShareIcon from '@mui/icons-material/Share';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalculateIcon from '@mui/icons-material/Calculate';
import HandshakeIcon from '@mui/icons-material/Handshake';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SEO from '../components/SEO';

// ─── Tiers (features only — no pricing) ──────────────────────────────────────

const getTiers = (t) => [
  {
    name: t('whitelabelSales.tiers.starter.name'),
    color: '#2b7bff',
    gradient: 'linear-gradient(135deg, rgba(43,123,255,0.15) 0%, rgba(43,123,255,0.05) 100%)',
    description: t('whitelabelSales.tiers.starter.description'),
    userLimit: t('whitelabelSales.tiers.starter.userLimit'),
    bots: t('whitelabelSales.tiers.starter.bots'),
    features: [
      t('whitelabelSales.tiers.starter.features.0'),
      t('whitelabelSales.tiers.starter.features.1'),
      t('whitelabelSales.tiers.starter.features.2'),
      t('whitelabelSales.tiers.starter.features.3'),
      t('whitelabelSales.tiers.starter.features.4'),
      t('whitelabelSales.tiers.starter.features.5'),
      t('whitelabelSales.tiers.starter.features.6'),
      t('whitelabelSales.tiers.starter.features.7'),
    ],
  },
  {
    name: t('whitelabelSales.tiers.growth.name'),
    color: '#f7b633',
    gradient: 'linear-gradient(135deg, rgba(247,182,51,0.18) 0%, rgba(247,182,51,0.06) 100%)',
    description: t('whitelabelSales.tiers.growth.description'),
    popular: true,
    userLimit: t('whitelabelSales.tiers.growth.userLimit'),
    bots: t('whitelabelSales.tiers.growth.bots'),
    features: [
      t('whitelabelSales.tiers.growth.features.0'),
      t('whitelabelSales.tiers.growth.features.1'),
      t('whitelabelSales.tiers.growth.features.2'),
      t('whitelabelSales.tiers.growth.features.3'),
      t('whitelabelSales.tiers.growth.features.4'),
      t('whitelabelSales.tiers.growth.features.5'),
      t('whitelabelSales.tiers.growth.features.6'),
      t('whitelabelSales.tiers.growth.features.7'),
    ],
  },
  {
    name: t('whitelabelSales.tiers.enterprise.name'),
    color: '#a855f7',
    gradient: 'linear-gradient(135deg, rgba(168,85,247,0.18) 0%, rgba(168,85,247,0.06) 100%)',
    description: t('whitelabelSales.tiers.enterprise.description'),
    userLimit: t('whitelabelSales.tiers.enterprise.userLimit'),
    bots: t('whitelabelSales.tiers.enterprise.bots'),
    features: [
      t('whitelabelSales.tiers.enterprise.features.0'),
      t('whitelabelSales.tiers.enterprise.features.1'),
      t('whitelabelSales.tiers.enterprise.features.2'),
      t('whitelabelSales.tiers.enterprise.features.3'),
      t('whitelabelSales.tiers.enterprise.features.4'),
      t('whitelabelSales.tiers.enterprise.features.5'),
      t('whitelabelSales.tiers.enterprise.features.6'),
      t('whitelabelSales.tiers.enterprise.features.7'),
    ],
  },
];

// ─── Feature modules ──────────────────────────────────────────────────────────

const getModules = (t) => [
  { icon: <BarChartIcon />, label: t('whitelabelSales.modules.journal.label'), desc: t('whitelabelSales.modules.journal.desc') },
  { icon: <SchoolIcon />, label: t('whitelabelSales.modules.academy.label'), desc: t('whitelabelSales.modules.academy.desc') },
  { icon: <PeopleIcon />, label: t('whitelabelSales.modules.community.label'), desc: t('whitelabelSales.modules.community.desc') },
  { icon: <TrendingUpIcon />, label: t('whitelabelSales.modules.optionFlow.label'), desc: t('whitelabelSales.modules.optionFlow.desc') },
  { icon: <ShareIcon />, label: t('whitelabelSales.modules.copyTrading.label'), desc: t('whitelabelSales.modules.copyTrading.desc') },
  { icon: <EmojiEventsIcon />, label: t('whitelabelSales.modules.competitions.label'), desc: t('whitelabelSales.modules.competitions.desc') },
  { icon: <ApiIcon />, label: t('whitelabelSales.modules.api.label'), desc: t('whitelabelSales.modules.api.desc') },
  { icon: <SecurityIcon />, label: t('whitelabelSales.modules.security.label'), desc: t('whitelabelSales.modules.security.desc') },
];

// ─── Testimonials ─────────────────────────────────────────────────────────────

const getTestimonials = (t) => [
  {
    name: 'Marcus K.',
    role: t('whitelabelSales.testimonials.0.role'),
    quote: t('whitelabelSales.testimonials.0.quote'),
    avatar: 'MK',
    color: '#2b7bff',
  },
  {
    name: 'Sarah L.',
    role: t('whitelabelSales.testimonials.1.role'),
    quote: t('whitelabelSales.testimonials.1.quote'),
    avatar: 'SL',
    color: '#f7b633',
  },
  {
    name: 'Tobias R.',
    role: t('whitelabelSales.testimonials.2.role'),
    quote: t('whitelabelSales.testimonials.2.quote'),
    avatar: 'TR',
    color: '#a855f7',
  },
];

// ─── ROI Calculator (earnings focus — no internal margins shown) ───────────────

function RoiCalculator() {
  const { t } = useTranslation();
  const [members, setMembers] = useState(200);
  const [avgPrice, setAvgPrice] = useState(49);

  const monthlyRevenue = members * avgPrice;
  const annualRevenue = monthlyRevenue * 12;

  return (
    <Box
      sx={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 3,
        p: { xs: 3, md: 5 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} mb={4}>
        <CalculateIcon sx={{ color: '#2b7bff', fontSize: 28 }} />
        <Typography variant="h5" fontWeight={700}>
          {t('whitelabelSales.roi.title')}
        </Typography>
      </Stack>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Stack spacing={4}>
            {/* Members slider */}
            <Box>
              <Stack direction="row" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">{t('whitelabelSales.roi.membersLabel')}</Typography>
                <Typography variant="body2" fontWeight={700} color="primary">{members}</Typography>
              </Stack>
              <Slider
                value={members}
                min={10}
                max={2000}
                step={10}
                onChange={(_, v) => setMembers(v)}
                sx={{ color: '#2b7bff' }}
              />
            </Box>

            {/* Price slider */}
            <Box>
              <Stack direction="row" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">{t('whitelabelSales.roi.avgPriceLabel')}</Typography>
                <Typography variant="body2" fontWeight={700} color="primary">${avgPrice}</Typography>
              </Stack>
              <Slider
                value={avgPrice}
                min={5}
                max={299}
                step={5}
                onChange={(_, v) => setAvgPrice(v)}
                sx={{ color: '#2b7bff' }}
              />
            </Box>

            <Box
              sx={{
                background: 'rgba(43,123,255,0.08)',
                border: '1px solid rgba(43,123,255,0.2)',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t('whitelabelSales.roi.platformNoteLabel')}
              </Typography>
              <Typography variant="body2" color="primary" fontWeight={600} mt={0.5}>
                {t('whitelabelSales.roi.platformNoteValue')}
              </Typography>
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack spacing={3} height="100%" justifyContent="center">
            <Box
              sx={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: 2,
                p: 3,
              }}
            >
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                {t('whitelabelSales.roi.grossMonthly')}
              </Typography>
              <Typography variant="h4" fontWeight={800} color="#4ade80">
                ${monthlyRevenue.toLocaleString()}
              </Typography>
            </Box>
            <Box
              sx={{
                background: 'rgba(43,123,255,0.1)',
                border: '1px solid rgba(43,123,255,0.3)',
                borderRadius: 2,
                p: 3,
              }}
            >
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                {t('whitelabelSales.roi.grossAnnual')}
              </Typography>
              <Typography variant="h4" fontWeight={800} color="#2b7bff">
                ${annualRevenue.toLocaleString()}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              href="#contact"
              endIcon={<ArrowForwardIcon />}
              sx={{
                background: 'linear-gradient(90deg, #2b7bff, #a855f7)',
                py: 1.5, fontWeight: 700, borderRadius: 2,
              }}
            >
              {t('whitelabelSales.roi.ctaLabel')}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function WhitelabelSales() {
  const { t } = useTranslation();
  const [contactForm, setContactForm] = useState({ name: '', email: '', community: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleContact = async (e) => {
    e.preventDefault();
    try {
      await apiFetch('/api/support/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: `Whitelabel-forespørsel: ${contactForm.community || contactForm.name}`,
          category: 'sales',
          priority: 'high',
          message: `Navn: ${contactForm.name}\nE-post: ${contactForm.email}\nCommunity: ${contactForm.community}\n\n${contactForm.message}`,
        }),
      });
    } catch (_) { /* best-effort */ }
    setSubmitted(true);
  };

  return (
    <>
      <SEO
        title={t('whitelabelSales.seo.title')}
        description={t('whitelabelSales.seo.description')}
      />

      {/* ── Hero ── */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, rgba(10,20,53,1) 0%, rgba(5,10,30,1) 100%)',
          pt: { xs: 10, md: 14 },
          pb: { xs: 8, md: 12 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)',
          width: 600, height: 600, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(43,123,255,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <Container maxWidth="md" sx={{ position: 'relative' }}>
          <Chip
            label={t('whitelabelSales.hero.badge')}
            sx={{ mb: 3, background: 'rgba(43,123,255,0.15)', border: '1px solid rgba(43,123,255,0.4)', color: '#7eb3ff' }}
          />
          <Typography variant="h2" fontWeight={800} sx={{ fontSize: { xs: '2.2rem', md: '3.5rem' }, lineHeight: 1.15, mb: 3 }}>
            {t('whitelabelSales.hero.headline1')}<br />
            <Box component="span" sx={{ background: 'linear-gradient(90deg, #2b7bff, #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {t('whitelabelSales.hero.headline2')}
            </Box><br />
            {t('whitelabelSales.hero.headline3')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 620, mx: 'auto', mb: 5, fontWeight: 400, lineHeight: 1.7 }}>
            {t('whitelabelSales.hero.subheadline')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              href="#roi"
              endIcon={<ArrowForwardIcon />}
              sx={{
                background: 'linear-gradient(90deg, #2b7bff, #1d5dd1)',
                px: 4, py: 1.5, fontSize: '1rem', fontWeight: 700, borderRadius: 2,
              }}
            >
              {t('whitelabelSales.hero.ctaRoi')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              href="#contact"
              sx={{ px: 4, py: 1.5, fontSize: '1rem', borderRadius: 2 }}
            >
              {t('whitelabelSales.hero.ctaDemo')}
            </Button>
          </Stack>

          {/* Stats bar */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={4}
            justifyContent="center"
            mt={8}
            divider={<Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />}
          >
            {[
              { value: t('whitelabelSales.stats.launchTime'), label: t('whitelabelSales.stats.launchLabel') },
              { value: '30+', label: t('whitelabelSales.stats.modulesLabel') },
              { value: '99.9%', label: t('whitelabelSales.stats.uptimeLabel') },
              { value: t('whitelabelSales.stats.languagesCount'), label: t('whitelabelSales.stats.languagesLabel') },
            ].map(s => (
              <Box key={s.label} textAlign="center">
                <Typography variant="h4" fontWeight={800} color="primary">{s.value}</Typography>
                <Typography variant="body2" color="text.secondary">{s.label}</Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ── Modules ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, background: 'rgba(255,255,255,0.02)' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={700} textAlign="center" mb={2}>
            {t('whitelabelSales.modules.heading')}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" mb={6}>
            {t('whitelabelSales.modules.subheading')}
          </Typography>
          <Grid container spacing={3}>
            {getModules(t).map((m) => (
              <Grid item xs={12} sm={6} md={3} key={m.label}>
                <Card sx={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ color: '#2b7bff', mb: 1.5 }}>{m.icon}</Box>
                    <Typography variant="subtitle1" fontWeight={700} mb={0.5}>{m.label}</Typography>
                    <Typography variant="body2" color="text.secondary">{m.desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── ROI Calculator ── */}
      <Box id="roi" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={700} textAlign="center" mb={2}>
            {t('whitelabelSales.roi.heading')}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" mb={6}>
            {t('whitelabelSales.roi.subheading')}
          </Typography>
          <RoiCalculator />
        </Container>
      </Box>

      {/* ── Packages / Features ── */}
      <Box id="packages" sx={{ py: { xs: 8, md: 12 }, background: 'rgba(255,255,255,0.02)' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={700} textAlign="center" mb={2}>
            {t('whitelabelSales.pricing.heading')}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" mb={6}>
            {t('whitelabelSales.pricing.subheading')}
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {getTiers(t).map((tier) => (
              <Grid item xs={12} md={4} key={tier.name}>
                <Card
                  sx={{
                    background: tier.gradient,
                    border: `1px solid ${tier.popular ? tier.color : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: 3,
                    position: 'relative',
                    height: '100%',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)' },
                  }}
                >
                  {tier.popular && (
                    <Chip
                      label={t('whitelabelSales.pricing.mostPopular')}
                      size="small"
                      sx={{
                        position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                        background: tier.color, color: '#fff', fontWeight: 700, fontSize: '0.7rem',
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="overline" sx={{ color: tier.color, fontWeight: 700, letterSpacing: 2 }}>
                      {tier.name}
                    </Typography>
                    <Stack direction="row" spacing={1} mt={1.5} mb={2} flexWrap="wrap" gap={0.5}>
                      <Chip label={tier.userLimit} size="small" sx={{ background: 'rgba(255,255,255,0.08)', fontSize: '0.72rem' }} />
                      <Chip label={tier.bots} size="small" sx={{ background: `${tier.color}22`, color: tier.color, fontSize: '0.72rem', border: `1px solid ${tier.color}44` }} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" mb={2.5}>{tier.description}</Typography>
                    <List dense disablePadding sx={{ mb: 3 }}>
                      {tier.features.map((f) => (
                        <ListItem key={f} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <CheckCircleIcon sx={{ fontSize: 16, color: tier.color }} />
                          </ListItemIcon>
                          <ListItemText primary={f} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                    <Divider sx={{ mb: 2.5, borderColor: 'rgba(255,255,255,0.08)' }} />
                    <Box
                      sx={{
                        background: `${tier.color}11`,
                        border: `1px solid ${tier.color}33`,
                        borderRadius: 2,
                        p: 1.5,
                        mb: 2,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                        {t('whitelabelSales.pricing.customPriceNote')}
                      </Typography>
                    </Box>
                    <Button
                      variant={tier.popular ? 'contained' : 'outlined'}
                      fullWidth
                      href="#contact"
                      sx={{
                        borderRadius: 2, fontWeight: 700,
                        ...(tier.popular
                          ? { background: tier.color, '&:hover': { background: tier.color, filter: 'brightness(0.9)' } }
                          : { borderColor: tier.color, color: tier.color }),
                      }}
                    >
                      {t('whitelabelSales.pricing.contactForPrice')}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" color="text.secondary" textAlign="center" mt={3}>
            {t('whitelabelSales.pricing.disclaimer')}
          </Typography>
        </Container>
      </Box>

      {/* ── Affiliate Program ── */}
      <Box id="affiliate" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} mb={2}>
            <HandshakeIcon sx={{ color: '#a855f7', fontSize: 32 }} />
            <Typography variant="overline" sx={{ color: '#a855f7', fontWeight: 700, letterSpacing: 2 }}>
              {t('whitelabelSales.affiliate.badge')}
            </Typography>
          </Stack>
          <Typography variant="h4" fontWeight={700} textAlign="center" mb={2}>
            {t('whitelabelSales.affiliate.heading')}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" mb={6} lineHeight={1.8}>
            {t('whitelabelSales.affiliate.description')}
          </Typography>
          <Grid container spacing={3}>
            {[
              { title: t('whitelabelSales.affiliate.features.0.title'), desc: t('whitelabelSales.affiliate.features.0.desc') },
              { title: t('whitelabelSales.affiliate.features.1.title'), desc: t('whitelabelSales.affiliate.features.1.desc') },
              { title: t('whitelabelSales.affiliate.features.2.title'), desc: t('whitelabelSales.affiliate.features.2.desc') },
              { title: t('whitelabelSales.affiliate.features.3.title'), desc: t('whitelabelSales.affiliate.features.3.desc') },
            ].map((item) => (
              <Grid item xs={12} sm={6} key={item.title}>
                <Stack direction="row" spacing={2} alignItems="flex-start"
                  sx={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.15)', borderRadius: 2, p: 2.5 }}>
                  <CheckCircleIcon sx={{ color: '#a855f7', mt: 0.3, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} mb={0.5}>{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Testimonials ── */}
      <Box sx={{ py: { xs: 8, md: 10 }, background: 'rgba(255,255,255,0.02)' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={700} textAlign="center" mb={6}>
            {t('whitelabelSales.testimonials.heading')}
          </Typography>
          <Grid container spacing={3}>
            {getTestimonials(t).map((item) => (
              <Grid item xs={12} md={4} key={item.name}>
                <Card sx={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="body1" color="text.secondary" mb={3} fontStyle="italic" lineHeight={1.7}>
                      "{item.quote}"
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box sx={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: '0.85rem', color: '#fff', flexShrink: 0,
                      }}>
                        {item.avatar}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.role}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Contact / Demo ── */}
      <Box id="contact" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          {/* Contact header with icon */}
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} mb={2}>
            <MailOutlineIcon sx={{ color: '#2b7bff', fontSize: 32 }} />
            <Typography variant="h4" fontWeight={700} textAlign="center">
              {t('whitelabelSales.contact.heading')}
            </Typography>
          </Stack>
          <Typography variant="body1" color="text.secondary" textAlign="center" mb={2}>
            {t('whitelabelSales.contact.subheading')}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" mb={6} sx={{ opacity: 0.7 }}>
            {t('whitelabelSales.contact.pricingNote')}
          </Typography>

          {submitted ? (
            <Box textAlign="center" py={6}>
              <CheckCircleIcon sx={{ fontSize: 56, color: '#4ade80', mb: 2 }} />
              <Typography variant="h5" fontWeight={700} mb={1}>{t('whitelabelSales.contact.successTitle')}</Typography>
              <Typography color="text.secondary">{t('whitelabelSales.contact.successBody')}</Typography>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handleContact}
              sx={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(43,123,255,0.3)',
                borderRadius: 3,
                p: { xs: 3, md: 5 },
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('whitelabelSales.contact.nameLabel')}
                    fullWidth
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm(p => ({ ...p, name: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('whitelabelSales.contact.emailLabel')}
                    type="email"
                    fullWidth
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm(p => ({ ...p, email: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('whitelabelSales.contact.communityLabel')}
                    fullWidth
                    value={contactForm.community}
                    onChange={(e) => setContactForm(p => ({ ...p, community: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('whitelabelSales.contact.messageLabel')}
                    multiline
                    rows={4}
                    fullWidth
                    value={contactForm.message}
                    onChange={(e) => setContactForm(p => ({ ...p, message: e.target.value }))}
                    placeholder={t('whitelabelSales.contact.messagePlaceholder')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      background: 'linear-gradient(90deg, #2b7bff, #a855f7)',
                      py: 1.5, fontSize: '1rem', fontWeight: 700, borderRadius: 2,
                    }}
                  >
                    {t('whitelabelSales.contact.submitButton')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
}
